import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { track } from '../utils/analytics';
import { UserContext } from '../App';

export default function Register() {
  const navigate = useNavigate();
  const { setCurrentUser, setUsers } = useContext(UserContext);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountType, setAccountType] = useState('owner');
  const [agreeTos, setAgreeTos] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState('');
  const [showTos, setShowTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    track('registration_viewed');
  }, []);

  const validate = () => {
    if (!name.trim()) return 'Full name is required';
    if (!email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email address';
    if (!password) return 'Password is required';
    if (password.length < 12) return 'Password must be at least 12 characters';
    if (password !== confirmPassword) return 'Passwords do not match';
    if (!agreeTos) return 'You must agree to the Terms of Service';
    if (!agreePrivacy) return 'You must confirm you have read the Privacy Policy';
    return '';
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) { setError(v); return; }
    try {
      setSubmitting(true);
      track('registration_submitted');

      // New Cognito-based signup flow
      const signupRes = await api.post('/auth/signup-cognito', {
        name: name.trim(),
        email: email.trim(),
        password,
        accountType,
        marketingConsent: marketing,
        tosVersion: '2025-09-01',
        privacyVersion: '2025-09-01'
      });

      track('registration_succeeded');

      // Do not log in locally; redirect to Cognito Hosted UI sign-in instead
      // so that Cognito issues the ID token with correct groups.
      const domain = (typeof window !== 'undefined' && window.__COGNITO_DOMAIN__) || process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
      if (!domain || !clientId) {
        // Fallback: show a simple message and send user to login page
        navigate('/login');
        return;
      }
      // Preserve email to pre-fill any future forms if needed
      try { sessionStorage.setItem('prefill_email', email.trim()); } catch {}
      navigate('/login?signup=1');
    } catch (err) {
      console.error('Registration error:', err?.response?.data || err?.message);
      setError('Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mist-100)' }}>
      <div style={{ background: '#fff', padding: 32, borderRadius: 16, boxShadow: 'var(--shadow-soft)', border: '1px solid var(--mist-200)', minWidth: 340, width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <img src="/logo.svg" alt="ProVault" style={{ height: 28 }} />
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 10px' }}>Create your account</h1>
        <form onSubmit={onSubmit} noValidate aria-describedby={error ? 'form-error' : undefined}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label htmlFor="reg-name" style={{ fontWeight: 600, color: 'var(--dusk-700)' }}>Full Name</label>
            <input id="reg-name" name="name" type="text" autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', fontSize: 16 }} required />

            <label htmlFor="reg-email" style={{ fontWeight: 600, color: 'var(--dusk-700)' }}>Email</label>
            <input id="reg-email" name="email" type="email" autoComplete="username" spellCheck={false} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', fontSize: 16 }} required />

            <label htmlFor="reg-password" style={{ fontWeight: 600, color: 'var(--dusk-700)' }}>Password</label>
            <input id="reg-password" name="new-password" type="password" autoComplete="new-password" minLength={12} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 12 characters" style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', fontSize: 16 }} required />
            <div style={{ fontSize: 12, color: 'var(--dusk-500)', marginTop: -6 }}>Tip: use a passphrase of 4+ words (16+ chars) for easier recall.</div>

            <label htmlFor="reg-confirm" style={{ fontWeight: 600, color: 'var(--dusk-700)' }}>Confirm Password</label>
            <input id="reg-confirm" name="confirm-new-password" type="password" autoComplete="new-password" minLength={12} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', fontSize: 16 }} required />

            <label htmlFor="account-type" style={{ fontWeight: 600, color: 'var(--dusk-700)' }}>Account type</label>
            <select
              id="account-type"
              value={accountType}
              onChange={e => setAccountType(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', fontSize: 16 }}
            >
              <option value="owner">Owner – I’m safeguarding my own estate</option>
              <option value="nominee">Nominee – I’m acting on someone else’s behalf</option>
            </select>

            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 4 }}>
              <input id="tos" type="checkbox" checked={agreeTos} onChange={e => setAgreeTos(e.target.checked)} aria-describedby="tos-help" required />
              <label htmlFor="tos" style={{ fontSize: 14 }}>
                I agree to the <button type="button" onClick={() => setShowTos(true)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--sage-700)', textDecoration: 'underline', cursor: 'pointer' }}>Terms of Service</button>
              </label>
            </div>
            <div id="tos-help" style={{ fontSize: 12, color: 'var(--dusk-500)' }}>Review the summary below. <a href="https://provault.io/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--sage-700)' }}>View full Terms</a>.</div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
              <input id="privacy" type="checkbox" checked={agreePrivacy} onChange={e => setAgreePrivacy(e.target.checked)} required />
              <label htmlFor="privacy" style={{ fontSize: 14 }}>
                I’ve read the <a href="https://provault.io/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--sage-700)', textDecoration: 'underline' }}>Privacy Policy</a>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
              <input id="marketing" type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} />
              <label htmlFor="marketing" style={{ fontSize: 14 }}>Send me product updates and tips (optional)</label>
            </div>

            {error && <div id="form-error" role="alert" style={{ color: '#d32f2f', fontWeight: 500, marginTop: 4 }}>{error}</div>}
            <button type="submit" disabled={submitting} style={{ marginTop: 8, width: '100%', padding: '12px 0', fontSize: 16, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer' }}>{submitting ? 'Creating...' : 'Create account'}</button>
            <div style={{ textAlign: 'center', fontSize: 14, marginTop: 8 }}>
              Already have an account? <button type="button" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: 'var(--sage-700)', textDecoration: 'underline', cursor: 'pointer' }}>Log in</button>
            </div>
          </div>
        </form>

        {showTos && (
          <div role="dialog" aria-modal="true" aria-labelledby="tos-title" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div style={{ background: '#fff', borderRadius: 12, width: 560, maxWidth: '90vw', maxHeight: '80vh', boxShadow: 'var(--shadow-soft)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--mist-200)' }}>
                <h2 id="tos-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>ProVault – Terms of Service</h2>
                <div style={{ fontSize: 12, color: 'var(--dusk-500)' }}>Effective date: 1st September 2025</div>
              </div>
              <div style={{ padding: 16, overflow: 'auto' }}>
                <p>Welcome to ProVault. By creating an account, you agree to the following terms:</p>
                <h3>Who we are</h3>
                <p>ProVault lets you securely store and manage financial accounts, insurance, investments and other important information.</p>
                <h3>Using ProVault</h3>
                <ul>
                  <li>You must be at least 16 years old.</li>
                  <li>You are responsible for keeping your login details safe. Do not share your account.</li>
                  <li>Do not use ProVault for illegal purposes.</li>
                </ul>
                <h3>Your Data</h3>
                <p>You own your data. We process and store it so that the service can work. We apply strict security measures to protect your information. Please review our Privacy Policy to understand how we handle your personal data.</p>
                <h3>Subscriptions & Payment</h3>
                <p>ProVault may be free during early access. Paid plans (if introduced later) will be clearly communicated with pricing and renewal terms.</p>
                <h3>Availability & Changes</h3>
                <p>We aim for high availability but cannot guarantee uninterrupted service. We may change, suspend or stop features as we improve the service. We will notify you in advance of major changes.</p>
                <h3>Ending Your Account</h3>
                <p>You can delete your account at any time. We may suspend or close accounts that breach these terms or are inactive for long periods.</p>
                <h3>Limitation of Liability</h3>
                <p>ProVault is provided “as is” without guarantees. We are not liable for indirect or incidental damages. Our total liability will not exceed the amount you’ve paid to us in the last 12 months (if any).</p>
                <h3>Changes to These Terms</h3>
                <p>We may update these Terms from time to time. We will notify you of material changes by email or within the app. Continuing to use ProVault after changes means you accept the new Terms.</p>
                <h3>Contact Us</h3>
                <p>If you have questions, contact us at: support@provault.io</p>
                <p><a href="https://provault.io/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--sage-700)' }}>View full Terms</a></p>
              </div>
              <div style={{ padding: 12, borderTop: '1px solid var(--mist-200)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowTos(false)} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid var(--mist-300)', background: '#fff', cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


